import * as React from 'react';
import { FindImagesModalController, FindImagesModalControllerProps } from './FindImagesModal.controller';
import { gql, NetworkStatus } from '@apollo/client';
import {
  GiphySearchInput,
  ImageType,
  Orientation,
  PixabaySearchInput,
  UnsplashSearchInput,
  useSearchGiphyLazyQuery,
  useSearchPixabayLazyQuery,
  useSearchUnsplashLazyQuery
} from '../../../../models/graphql';
import { MediaGridContext } from '../MediaGrid/MediaGrid.view';
import { Void } from '../../../../models/basic-objects';
import { useCallback, useState } from 'react';
import { FindImagesModalViewProps } from './FindImagesModal.view';
import { MediaItemSelectedType } from '../../../../models/media';

export type FindImagesModalApolloProps = {
  context: MediaGridContext;
  onClose: Void;
  fileUploadHandler: FindImagesModalControllerProps['fileUploadHandler'];
  shareItemAction: FindImagesModalViewProps['shareItemAction'];
  imagesIdsWithErrorSelected: MediaItemSelectedType[];
  setImagesIdsWithErrorSelected: React.Dispatch<React.SetStateAction<MediaItemSelectedType[]>>;
};

gql`
  query SearchPixabay($input: PixabaySearchInput!) {
    searchPixabay(input: $input) {
      ...CoreThirdPartySearchFields
    }
  }

  query SearchGiphy($input: GiphySearchInput!) {
    searchGiphy(input: $input) {
      ...CoreThirdPartySearchFields
    }
  }

  query SearchUnsplash($input: UnsplashSearchInput!) {
    searchUnsplash(input: $input) {
      ...CoreThirdPartySearchFields
    }
  }
`;

const maxPixabayResultCount = 100;
const maxGiphyResultCount = 50;
const maxUnsplashResultCount = 30;

export const FindImagesModalApollo = (props: FindImagesModalApolloProps): JSX.Element => {
  const [page, setPage] = useState<number>(1);
  const [hasReachedFetchLimit, setHasReachedFetchLimit] = useState<boolean>(false);
  const [searchPixabay, searchPixabayResults] = useSearchPixabayLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });
  const [searchGiphy, searchGiphyResults] = useSearchGiphyLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });
  const [searchUnsplash, searchUnsplashResults] = useSearchUnsplashLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });

  const initialPixabayImagesSearch = useCallback(
    (filters: { orientation: Orientation; type: ImageType }, term: string) => {
      const orientationFilter = filters.orientation || null;
      const typeFilter = filters.type || null;

      const input: PixabaySearchInput = {
        term: term,
        imageType: typeFilter,
        orientation: orientationFilter,
        perPage: maxPixabayResultCount,
        page: 1
      };

      setPage(1);

      searchPixabay({
        variables: { input: input }
      }).then(results => {
        const itemCount = results.data?.searchPixabay.length || 0;
        setHasReachedFetchLimit(() => itemCount < maxPixabayResultCount);
      });
    },
    [searchPixabay]
  );

  const fetchMorePixabayImages = useCallback(() => {
    const { fetchMore: searchPixabayResultsFetchMore, variables: searchPixabayResultsVariables } = searchPixabayResults;

    if (hasReachedFetchLimit || !searchPixabayResults || !searchPixabayResultsVariables) {
      return;
    }

    searchPixabayResultsFetchMore({
      variables: {
        ...searchPixabayResultsVariables,
        input: {
          ...searchPixabayResultsVariables.input,
          page: page + 1
        }
      }
    })
      .finally(() => {
        setPage(page => page + 1);
      })
      .then(results => {
        const itemCount = results.data?.searchPixabay.length || 0;
        setHasReachedFetchLimit(() => itemCount < maxPixabayResultCount);
      });
  }, [hasReachedFetchLimit, page, searchPixabayResults]);

  const initialGiphyImagesSearch = useCallback(
    (term: string) => {
      const input: GiphySearchInput = {
        term: term,
        perPage: maxGiphyResultCount,
        page: 1
      };

      setPage(1);

      searchGiphy({
        variables: { input: input }
      }).then(results => {
        const itemCount = results.data?.searchGiphy.length || 0;
        setHasReachedFetchLimit(() => itemCount < maxGiphyResultCount);
      });
    },
    [searchGiphy]
  );

  const fetchMoreGiphyImages = useCallback(() => {
    const { fetchMore: searchGiphyResultsFetchMore, variables: searchGiphyResultsVariables } = searchGiphyResults;

    if (hasReachedFetchLimit || !searchGiphyResults || !searchGiphyResultsVariables) {
      return;
    }

    searchGiphyResultsFetchMore({
      variables: {
        ...searchGiphyResultsVariables,
        input: {
          ...searchGiphyResultsVariables.input,
          page: page + 1
        }
      }
    })
      .finally(() => {
        setPage(page => page + 1);
      })
      .then(results => {
        const itemCount = results.data?.searchGiphy.length || 0;
        setHasReachedFetchLimit(() => itemCount < maxGiphyResultCount);
      });
  }, [hasReachedFetchLimit, page, searchGiphyResults]);

  const initialUnsplashImagesSearch = useCallback(
    (term: string) => {
      const input: UnsplashSearchInput = {
        term: term,
        perPage: maxUnsplashResultCount,
        page: 1
      };

      setPage(1);

      searchUnsplash({
        variables: { input: input }
      }).then(results => {
        const itemCount = results.data?.searchUnsplash.length || 0;
        setHasReachedFetchLimit(() => itemCount < maxUnsplashResultCount);
      });
    },
    [searchUnsplash]
  );

  const fetchMoreUnsplashImages = useCallback(() => {
    const { fetchMore: searchUnsplashResultsFetchMore, variables: searchUnsplashResultsVariables } =
      searchUnsplashResults;

    if (hasReachedFetchLimit || !searchGiphyResults || !searchUnsplashResultsVariables) {
      return;
    }

    searchUnsplashResultsFetchMore({
      variables: {
        ...searchUnsplashResultsVariables,
        input: {
          ...searchUnsplashResultsVariables.input,
          page: page + 1
        }
      }
    })
      .finally(() => {
        setPage(page => page + 1);
      })
      .then(results => {
        const itemCount = results.data?.searchUnsplash.length || 0;
        setHasReachedFetchLimit(() => itemCount < maxUnsplashResultCount);
      });
  }, [hasReachedFetchLimit, page, searchGiphyResults, searchUnsplashResults]);

  const loadingMore =
    searchPixabayResults.networkStatus === NetworkStatus.fetchMore ||
    searchUnsplashResults.networkStatus === NetworkStatus.fetchMore ||
    searchGiphyResults.networkStatus === NetworkStatus.fetchMore;
  const loadingInitial =
    !loadingMore && (searchPixabayResults.loading || searchUnsplashResults.loading || searchGiphyResults.loading);

  const controllerProps: FindImagesModalControllerProps = {
    ...props,
    loadingInitial,
    loadingMore,
    initialPixabayImagesSearch,
    initialGiphyImagesSearch,
    initialUnsplashImagesSearch,
    fetchMorePixabayImages,
    fetchMoreGiphyImages,
    fetchMoreUnsplashImages,
    searchPixabayResults,
    searchGiphyResults,
    searchUnsplashResults
  };

  return <FindImagesModalController {...controllerProps} />;
};
